import { AssetAttributes } from '@usertour/frame';
import {
  ContentEditorSerialize,
  LinkDecoratorContext,
  PopperMadeWith,
  ChecklistProgress,
  ChecklistDismiss,
  ChecklistDropdown,
  ChecklistItems,
  ChecklistPopperContentBody,
  ChecklistPopperUseIframe,
  ChecklistRoot,
  WidgetLocaleProvider,
} from '@usertour/widget';
import {
  ChecklistData,
  ChecklistItemType,
  ContentEditorClickableElement,
  ThemeTypesSetting,
  UserTourTypes,
} from '@usertour/types';
import { useSyncExternalStore } from 'react';
import { UsertourChecklist } from '@/core/usertour-checklist';

// Types
type ChecklistWidgetProps = {
  checklist: UsertourChecklist;
};

type ChecklistWidgetCoreProps = {
  data: ChecklistData;
  themeSettings: ThemeTypesSetting;
  userAttributes?: UserTourTypes.Attributes;
  assets: AssetAttributes[] | undefined;
  handleItemClick: (item: ChecklistItemType, index: number) => void;
  handleOnClick: ({ type, data }: ContentEditorClickableElement) => Promise<void>;
  handleDismiss: () => Promise<void>;
  handleExpandedChange: (expanded: boolean) => Promise<void>;
  handleAutoDismiss: () => Promise<void>;
  removeBranding: boolean;
  zIndex: number;
  expanded: boolean;
  linkUrlDecorator?: ((url: string) => string) | null;
};

// Custom hook to extract store state
const useChecklistStore = (checklist: UsertourChecklist) => {
  const store = useSyncExternalStore(checklist.subscribe, checklist.getSnapshot);

  if (!store) {
    return null;
  }

  const {
    userAttributes,
    checklistData,
    expanded,
    openState,
    zIndex,
    globalStyle,
    themeSettings,
    assets,
    removeBranding,
    linkUrlDecorator,
    userLocale,
  } = store;

  if (!checklistData || !openState) {
    return null;
  }

  return {
    userAttributes,
    checklistData,
    expanded,
    openState,
    zIndex,
    globalStyle,
    themeSettings,
    assets,
    removeBranding,
    linkUrlDecorator,
    userLocale,
  };
};

// Components
const ChecklistContent = ({
  data,
  userAttributes,
  handleOnClick,
  handleItemClick,
  handleAutoDismiss,
}: Pick<
  ChecklistWidgetCoreProps,
  'data' | 'userAttributes' | 'handleOnClick' | 'handleItemClick' | 'handleAutoDismiss'
>) => (
  <>
    <ContentEditorSerialize
      contents={data.content}
      onClick={handleOnClick}
      userAttributes={userAttributes}
    />
    <ChecklistProgress />
    <ChecklistItems onClick={handleItemClick} disabledUpdate={true} />
    <ChecklistDismiss onAutoDismiss={handleAutoDismiss} />
  </>
);

const ChecklistWidgetCore = ({
  data,
  themeSettings,
  userAttributes,
  assets,
  handleItemClick,
  handleOnClick,
  handleDismiss,
  handleExpandedChange,
  removeBranding,
  zIndex,
  expanded,
  handleAutoDismiss,
  linkUrlDecorator,
}: ChecklistWidgetCoreProps) => (
  <LinkDecoratorContext.Provider value={linkUrlDecorator || null}>
    <ChecklistRoot
      data={data}
      themeSettings={themeSettings}
      expanded={expanded}
      onDismiss={handleDismiss}
      onExpandedChange={handleExpandedChange}
      zIndex={zIndex}
    >
      <ChecklistPopperUseIframe zIndex={zIndex} assets={assets}>
        <ChecklistDropdown />
        <ChecklistPopperContentBody>
          <ChecklistContent
            data={data}
            userAttributes={userAttributes}
            handleOnClick={handleOnClick}
            handleItemClick={handleItemClick}
            handleAutoDismiss={handleAutoDismiss}
          />
        </ChecklistPopperContentBody>
        {!removeBranding && <PopperMadeWith />}
      </ChecklistPopperUseIframe>
    </ChecklistRoot>
  </LinkDecoratorContext.Provider>
);

export const ChecklistWidget = ({ checklist }: ChecklistWidgetProps) => {
  const store = useChecklistStore(checklist);

  if (!store) {
    return <></>;
  }

  const {
    checklistData,
    themeSettings,
    userAttributes,
    openState,
    assets,
    zIndex,
    expanded,
    removeBranding,
    userLocale,
    linkUrlDecorator,
  } = store;

  if (!themeSettings || !checklistData || !openState || !userAttributes) {
    return <></>;
  }

  return (
    <WidgetLocaleProvider locale={userLocale}>
      <ChecklistWidgetCore
        data={checklistData}
        themeSettings={themeSettings}
        userAttributes={userAttributes}
        assets={assets}
        handleItemClick={checklist.handleItemClick}
        handleOnClick={checklist.handleOnClick}
        handleDismiss={checklist.handleDismiss}
        handleExpandedChange={checklist.handleExpandedChange}
        removeBranding={removeBranding}
        zIndex={zIndex}
        expanded={expanded}
        handleAutoDismiss={checklist.handleAutoDismiss}
        linkUrlDecorator={linkUrlDecorator}
      />
    </WidgetLocaleProvider>
  );
};
